import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useToast } from "../../context/ToastContext";

const GrnForm = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { showToast } = useToast();
  const user         = JSON.parse(localStorage.getItem("user"));
  const [poDetails, setPoDetails]             = useState(null);
  const [grnItems, setGrnItems]               = useState([]);
  const [grnNumber, setGrnNumber]             = useState("");
  const [grnDate, setGrnDate]                 = useState(null);
  const [notes, setNotes]                     = useState("");
  const [fullyReceived, setFullyReceived]     = useState(false);
  const [challanInvoiceNo, setChallanInvoiceNo]     = useState("");
  const [challanInvoiceDate, setChallanInvoiceDate] = useState(null);
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [loading, setLoading]                 = useState(true);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        const poRes = await axios.get(`/purchase-orders/${id}`);
        const { order, items } = poRes.data;
        setPoDetails(order);

        const receivedRes = await axios.get(`/grns/received-quantities/${id}`);
        const receivedMap = {};
        receivedRes.data.items.forEach((i) => {
          receivedMap[i.purchase_order_item_id] = parseFloat(i.received_qty) || 0;
        });

        const merged = items.map((i) => {
          const prev      = receivedMap[i.id] || 0;
          const remaining = parseFloat(i.qty) - prev;
          return { ...i, previously_received: prev, received_quantity: remaining };
        });

        setGrnItems(merged);
        setFullyReceived(merged.every((item) => item.received_quantity === 0));

        const countRes = await axios.get(`/grns/count-by-po/${order.id}`);
        const count    = countRes.data.count + 1;
        setGrnNumber(`GRN/${String(count).padStart(2, "0")}/${order.po_number}`);
      } catch (err) {
        console.error("Error loading GRN form:", err);
        showToast("error", "Load Failed", "Failed to load GRN form data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatDate = (date) => {
    if (!date) return null;
    const yyyy = date.getFullYear();
    const mm   = String(date.getMonth() + 1).padStart(2, "0");
    const dd   = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleQuantityChange = (index, value) => {
    const updated  = [...grnItems];
    const entered  = parseFloat(value) || 0;
    const remaining = parseFloat(updated[index].qty) - parseFloat(updated[index].previously_received || 0);
    if (entered > remaining) {
      showToast("warning", "Exceeds Remaining", `Max allowed for this item: ${remaining}`);
      updated[index].received_quantity = 0;
    } else {
      updated[index].received_quantity = entered;
    }
    setGrnItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!grnDate) {
      showToast("warning", "Date Required", "Please select a GRN date.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post("/grns", {
        purchase_order_id:    poDetails.id,
        created_by:           user.id,
        grn_number:           grnNumber,
        grn_date:             formatDate(grnDate),
        challan_invoice_no:   challanInvoiceNo,
        challan_invoice_date: formatDate(challanInvoiceDate),
        notes,
        items: grnItems.map((i) => ({
          purchase_order_item_id: i.id,
          received_quantity:      i.received_quantity,
        })),
      });
      showToast("success", "GRN Created", `${grnNumber} has been recorded successfully.`);
      navigate("/dashboard/grns");
    } catch (err) {
      console.error("GRN creation error:", err);
      showToast("error", "Failed", "Could not create GRN. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Create GRN</h4>
          <p>Record goods received against {poDetails?.po_number}</p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/grns")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── PO Info + GRN header ── */}
        <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
          <div className="card-body">
            <div className="row">
              {/* GRN Number */}
              <div className="col-md-4 mb-3">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>GRN Number</label>
                <input className="form-control form-control-sm" value={grnNumber} readOnly
                  style={{ background: "#f8fafc", fontWeight: 700 }} />
              </div>

              {/* GRN Date */}
              <div className="col-md-4 mb-3">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>
                  GRN Date <span className="text-danger">*</span>
                </label>
                <DatePicker
                  selected={grnDate}
                  onChange={(date) => setGrnDate(date)}
                  className="form-control form-control-sm"
                  dateFormat="dd-MM-yyyy"
                  maxDate={new Date()}
                  placeholderText="Select GRN Date"
                  required
                />
              </div>

              {/* PO Info */}
              {poDetails && (
                <div className="col-md-4 mb-3">
                  <label style={{ fontWeight: 600, fontSize: "13px" }}>Purchase Order</label>
                  <div className="p-2 rounded" style={{ background: "#f8fafc", fontSize: "12px", border: "1px solid #e2e8f0" }}>
                    <strong>{poDetails.po_number}</strong><br />
                    Vendor: {poDetails.vendor_name}<br />
                    Date: {new Date(poDetails.order_date).toLocaleDateString("en-GB")}
                  </div>
                </div>
              )}
            </div>

            {/* Challan / Invoice */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>Challan / Invoice No</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={challanInvoiceNo}
                  onChange={(e) => setChallanInvoiceNo(e.target.value)}
                  placeholder="Enter challan or invoice number"
                />
              </div>
              <div className="col-md-4 mb-3">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>Challan / Invoice Date</label>
                <DatePicker
                  selected={challanInvoiceDate}
                  onChange={(date) => setChallanInvoiceDate(date)}
                  className="form-control form-control-sm"
                  dateFormat="dd-MM-yyyy"
                  maxDate={new Date()}
                  placeholderText="Select date"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Items table ── */}
        <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
          <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
            <strong style={{ fontSize: "14px" }}>
              <i className="fas fa-boxes mr-2 text-primary"></i>Items
            </strong>
          </div>
          <div className="card-body p-0">
            {fullyReceived ? (
              <div className="p-4 text-center">
                <span className="ims-badge ims-badge-full" style={{ fontSize: "13px", padding: "6px 16px" }}>
                  <i className="fas fa-check-circle mr-2"></i>
                  All items fully received — no further GRN needed
                </span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="ims-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Ordered</th>
                      <th>Previously Received</th>
                      <th>Receiving Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grnItems.map((item, i) => {
                      const remaining = parseFloat(item.qty) - parseFloat(item.previously_received || 0);
                      return (
                        <tr key={item.id}>
                          <td><strong>{item.item_name}</strong></td>
                          <td>{item.qty}</td>
                          <td>{item.previously_received}</td>
                          <td style={{ width: "150px" }}>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: "110px" }}
                              value={item.received_quantity}
                              onChange={(e) => handleQuantityChange(i, e.target.value)}
                              min="0"
                              max={remaining}
                              step="0.01"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── Notes + Submit ── */}
        {!fullyReceived && (
          <div className="card shadow-sm" style={{ border: "none", borderRadius: "12px" }}>
            <div className="card-body">
              <div className="mb-3">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>Notes</label>
                <textarea
                  className="form-control form-control-sm"
                  rows="3"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes (optional)"
                />
              </div>
              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm mr-2"
                  onClick={() => navigate("/dashboard/grns")}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={isSubmitting}>
                  {isSubmitting
                    ? <><i className="fas fa-spinner fa-spin mr-1"></i> Saving...</>
                    : <><i className="fas fa-save mr-1"></i> Submit GRN</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default GrnForm;
